import React, { useMemo, useState } from "react";

import { useLocalization } from "@fluent/react";
import { Button, Chip, LoadingSkeleton, Radio } from "@narmi/design_system";
import { Dialog } from "cerulean";

import { getConfidenceChip } from "../../confidence";
import {
  formatDelta,
  formatReceiptAmount,
  formatReceiptDate,
} from "../../format";
import { useLinkMatch } from "../../hooks/useLinkMatch";
import { useMatchCandidates } from "../../hooks/useMatchCandidates";

import TransactionSearchSection from "./TransactionSearchSection";
import styles from "./SelectTransactionModal.module.scss";

import type {
  DisplacedReceipt,
  LinkMatchError,
} from "../../hooks/useLinkMatch";
import type { MatchCandidate } from "../../hooks/useMatchCandidates";
import type { ReceiptDetail } from "../../hooks/useReceipt";

interface SelectTransactionModalProps {
  receiptId: string;
  /** The receipt's currently presented top suggestion, hidden from the list. */
  suggestedTransactionId?: string | null;
  onClose: () => void;
  onLinkSuccess: (updated: ReceiptDetail) => void;
  onLinkError: (error: LinkMatchError) => void;
}

type ModalSelection =
  | { kind: "candidate"; transactionId: string }
  | { kind: "search"; transaction: API.Transaction };

interface SwapConflict {
  transactionId: string;
  displacedReceipt: DisplacedReceipt;
}

const CandidateRow = ({
  candidate,
  isSelected,
  onCheck,
}: {
  candidate: MatchCandidate;
  isSelected: boolean;
  onCheck: (transactionId: string) => void;
}) => {
  const { l10n } = useLocalization();
  const { transaction } = candidate;
  const confidence = getConfidenceChip(candidate.confidence_score);
  const delta = formatDelta(candidate.amount_delta);

  return (
    <Radio
      name="select-transaction-candidate"
      value={transaction.id}
      kind="input-card"
      checked={isSelected}
      onCheck={onCheck}
    >
      <div
        className={styles["candidate-content"]}
        data-testid={`select-transaction-candidate-${transaction.id}`}
      >
        <div className={styles["candidate-line"]}>
          <span className="fontColor--heading fontWeight--semibold">
            {transaction.description}
          </span>
          <span className="fontColor--heading fontWeight--semibold">
            {formatReceiptAmount(transaction.amount)}
          </span>
        </div>
        <div className={styles["candidate-line"]}>
          <span className="fontColor--primary fontSize--s">
            {formatReceiptDate(transaction.date)} · {transaction.account.name}
          </span>
          <span className={styles["candidate-chips"]}>
            {delta && <Chip kind={delta.kind} label={delta.display} />}
            <Chip
              kind={confidence.kind}
              label={l10n.getString(confidence.labelKey)}
            />
          </span>
        </div>
      </div>
    </Radio>
  );
};

const SelectTransactionModal = ({
  receiptId,
  suggestedTransactionId = null,
  onClose,
  onLinkSuccess,
  onLinkError,
}: SelectTransactionModalProps) => {
  const { l10n } = useLocalization();
  const [selection, setSelection] = useState<ModalSelection | null>(null);
  // The last search pick stays rendered as a card when a candidate takes the
  // selection, so the user can click back to it.
  const [searchChoice, setSearchChoice] = useState<API.Transaction | null>(
    null
  );
  const [conflict, setConflict] = useState<SwapConflict | null>(null);
  const { data, isLoading, isError, refetch } = useMatchCandidates(receiptId);

  const candidates = useMemo(
    () =>
      (data?.candidates ?? []).filter(
        (candidate) => candidate.transaction.id !== suggestedTransactionId
      ),
    [data, suggestedTransactionId]
  );

  let selectedTransactionId: string | null = null;
  if (selection !== null) {
    selectedTransactionId =
      selection.kind === "candidate"
        ? selection.transactionId
        : selection.transaction.id;
  }

  // A retry refetch can drop the selected candidate from the list; a
  // candidate selection only counts while its row is still present.
  const isSelectionActionable =
    selection !== null &&
    (selection.kind === "search" ||
      candidates.some(
        (candidate) => candidate.transaction.id === selection.transactionId
      ));

  // The swap warning shows when the selected search result visibly holds a
  // receipt, or when a confirm attempt on this transaction came back 409
  // (a receipt the visibility-scoped search response could not show).
  const isSwapArmed =
    isSelectionActionable &&
    ((selection.kind === "search" && selection.transaction.receipt != null) ||
      conflict?.transactionId === selectedTransactionId);

  const linkMatch = useLinkMatch({
    onSuccess: onLinkSuccess,
    onError: (error, variables) => {
      if (
        error.status === 409 &&
        error.errorCode === "transaction_has_receipt" &&
        error.displacedReceipt
      ) {
        // Key the conflict to the transaction that was mutated. The user can
        // change the selection while the request is in flight.
        setConflict({
          transactionId: variables.transactionId,
          displacedReceipt: error.displacedReceipt,
        });
        return;
      }
      onLinkError(error);
    },
  });

  const handleSelectCandidate = (transactionId: string) => {
    setSelection({ kind: "candidate", transactionId });
  };

  const handleSelectSearchResult = (transaction: API.Transaction) => {
    setSearchChoice(transaction);
    setSelection({ kind: "search", transaction });
  };

  const handleConfirm = () => {
    if (!isSelectionActionable || selectedTransactionId === null) return;
    if (linkMatch.isPending) return;
    linkMatch.mutate({
      receiptId,
      transactionId: selectedTransactionId,
      allowSwap: isSwapArmed,
    });
  };

  const handleDismiss = () => {
    // Gate dismiss on pending so the modal can't disappear out from under an
    // in-flight link. The toast shows while the modal is still on screen.
    if (linkMatch.isPending) return;
    onClose();
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div data-testid="select-transaction-modal-loading">
          <LoadingSkeleton isLoading content="paragraph" lines={4} />
        </div>
      );
    }

    const searchSection = (
      <TransactionSearchSection
        selectedTransaction={searchChoice}
        isSelected={selection?.kind === "search"}
        showSwapNotice={Boolean(isSwapArmed)}
        onSelect={handleSelectSearchResult}
      />
    );

    if (isError) {
      return (
        <div>
          <div
            className={styles.error}
            data-testid="select-transaction-modal-error"
          >
            <p className="fontColor--primary">
              {l10n.getString("receipts-select-transaction-error-load")}
            </p>
            <Button
              kind="secondary"
              label={l10n.getString("receipts-drawer-error-retry")}
              onClick={() => refetch()}
              testId="select-transaction-modal-retry"
            />
          </div>
          <div className={styles.divider} />
          {searchSection}
        </div>
      );
    }

    if (candidates.length === 0) {
      return searchSection;
    }

    return (
      <div>
        <p
          className={`${styles["section-label"]} fontColor--secondary fontWeight--bold fontSize--xs margin--bottom--s`}
        >
          {l10n.getString("receipts-select-transaction-suggested-heading")}
        </p>
        <div
          className={styles["candidate-list"]}
          role="radiogroup"
          aria-label={l10n.getString(
            "receipts-select-transaction-suggested-heading"
          )}
        >
          {candidates.map((candidate) => (
            <CandidateRow
              key={candidate.transaction.id}
              candidate={candidate}
              isSelected={
                selection?.kind === "candidate" &&
                selection.transactionId === candidate.transaction.id
              }
              onCheck={handleSelectCandidate}
            />
          ))}
        </div>
        <div className={styles.divider} />
        {searchSection}
      </div>
    );
  };

  return (
    <Dialog
      isOpen
      title={l10n.getString("receipts-select-transaction-title")}
      headerStyle="bordered"
      width="593px"
      onUserDismiss={handleDismiss}
      testId="select-transaction-modal"
      footer={
        <div className={styles.footer}>
          <Button
            kind="secondary"
            label={l10n.getString("receipts-select-transaction-close")}
            onClick={onClose}
            disabled={linkMatch.isPending}
            testId="select-transaction-modal-close"
          />
          <Button
            kind="primary"
            label={l10n.getString("receipts-select-transaction-confirm")}
            onClick={handleConfirm}
            disabled={!isSelectionActionable || linkMatch.isPending}
            isLoading={linkMatch.isPending}
            testId="select-transaction-modal-confirm"
          />
        </div>
      }
    >
      {renderBody()}
    </Dialog>
  );
};

export default SelectTransactionModal;
